import { FarmRules } from '@agroone/business'
import { Crop } from '@agroone/entities'
import { FarmService } from '../../../../../shared/farm/services/farm.service'
import { startLoadingFarms, stopLoadingFarms } from '../../../../../state/loading/loading.actions'
import { LoadingState } from '../../../../../state/loading/loading.reducer'
import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { catchError, map, mergeMap, of, tap } from 'rxjs'
import { mapCrops } from '../../../crop/store/actions/crop.actions'
import {
  addFarm,
  addFarmFailure,
  addFarmSuccess,
  loadFarms,
  loadFarmsFailure,
  loadFarmsSuccess,
  updateFarm,
  updateFarmFailure,
  updateFarmSuccess,
} from '../actions/farm.actions'
import { requireDefined, SharedUserService } from '@agroone-front/shared'

@Injectable()
export class FarmEffects {
  private actions$ = inject(Actions)
  private farmService = inject(FarmService)
  private loadingStore = inject<Store<LoadingState>>(Store)
  private userService = inject(SharedUserService)

  public getFarms$ = createEffect(() => {
    this.loadingStore.dispatch(startLoadingFarms())

    return this.actions$.pipe(
      ofType(loadFarms),
      mergeMap(() =>
        this.farmService
          .getAll(FarmRules.canManageInactiveFarms(this.userService.currentUser?.permissions ?? []) === false)
          .pipe(
            tap(() => this.loadingStore.dispatch(stopLoadingFarms())),
            map((farms) => loadFarmsSuccess({ farms })),
            catchError((e) => {
              this.loadingStore.dispatch(stopLoadingFarms())
              return of(loadFarmsFailure({ error: `${e}` }))
            }),
          ),
      ),
    )
  })

  public addFarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFarm),
      mergeMap(({ payload }) =>
        this.farmService.save(payload.farm).pipe(
          map((farm) =>
            addFarmSuccess({
              payload: {
                farm,
              },
            }),
          ),
          catchError((e) =>
            of(
              addFarmFailure({
                error: `${e}`,
              }),
            ),
          ),
        ),
      ),
    ),
  )

  public reloadFarmsAfterSaveSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addFarmSuccess, updateFarmSuccess),
      map(() => loadFarms()),
    ),
  )

  public updateFarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFarm),
      mergeMap(({ payload }) =>
        this.farmService.save(payload.farm).pipe(
          map((farm) =>
            updateFarmSuccess({
              payload: {
                farm,
                loading: false,
              },
            }),
          ),
          catchError((e) =>
            of(
              updateFarmFailure({
                error: `${e}`,
                loading: false,
              }),
            ),
          ),
        ),
      ),
    ),
  )

  public updateCropsCooperativeAfterFarmUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateFarmSuccess),
      map(({ payload }) => {
        const farm = payload.farm
        const sortedCooperatives = [...(farm.cooperatives ?? [])].sort((a, b) =>
          requireDefined(a.name, 'the cooperative name').localeCompare(String(b.name)),
        )
        const cropTypeToCooperative = new Map<number, { id: number; name: string | undefined }>()
        for (const coop of sortedCooperatives) {
          for (const cropType of coop.cropTypes ?? []) {
            if (!cropTypeToCooperative.has(cropType.id)) {
              cropTypeToCooperative.set(cropType.id, { id: coop.id, name: coop.name })
            }
          }
        }
        return mapCrops({
          entityMap: (crop) => {
            if (crop.farmId !== farm.id) return crop
            const cooperative = crop.cropTypeId != null ? cropTypeToCooperative.get(crop.cropTypeId) : undefined
            return {
              ...crop,
              cooperativeId: cooperative?.id ?? null,
              cooperativeName: cooperative?.name ?? null,
            } as Crop
          },
        })
      }),
    ),
  )
}
